import {useState} from 'react'
import type {TranslateT} from '@freckle/educator-entities/ts/common/helpers/translate'
import {Dropdown} from '@freckle/educator-materials/ts/components/dropdown'
import {useSafeTranslation} from '@freckle/educator-materials/ts/helpers/translate'
import type {OptionType} from '@freckle/educator-materials/ts/logic/dropdown'
import {flatOptions} from '@freckle/educator-materials/ts/logic/dropdown'
import type {SummaryT} from '@freckle/school/ts/common/models/summary'
import type {IdentityProviderOrNoneT} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import {
  NO_IDENTITY_PROVIDER_VALUE,
  setSourceFilterInLocalStorage,
} from '@freckle/school/ts/manage-teachers/api-helpers/filters'
import {getPossibleSources} from './utils'
import {sourceDropdown} from './source-dropdown.module.scss'

type Props = {
  id: string
  initSources: Array<IdentityProviderOrNoneT>
  routeFn: (value: Array<IdentityProviderOrNoneT>) => void
  summary: SummaryForEntityT
  labelText: string
}

type SummaryForEntityTagT = 'for-teacher' | 'for-student' | 'for-course'

export type SummaryForEntityT = {
  tag: SummaryForEntityTagT
} & SummaryT

const display = (t: TranslateT, value: IdentityProviderOrNoneT): string =>
  value === NO_IDENTITY_PROVIDER_VALUE
    ? t('MANAGE_TEACHERS_SOURCE_DROPDOWN_TEACHER_SOURCE')
    : t('DISPLAY_IDP', {idp: value})

export function SourceDropdown({initSources, routeFn, id, summary, labelText}: Props) {
  const {t} = useSafeTranslation()

  const sourceToValue = (source: IdentityProviderOrNoneT) => ({
    label: display(t, source),
    value: source,
  })
  const possibleSources = getPossibleSources(summary).map(sourceToValue)

  const [selection, setSelection] = useState<Array<OptionType<IdentityProviderOrNoneT>>>(
    initSources.map(sourceToValue),
  )

  const toggle = (values: readonly OptionType<IdentityProviderOrNoneT>[]) => {
    setSelection([...values])
    const idpValues: IdentityProviderOrNoneT[] = values.map(({value}) => value)
    setSourceFilterInLocalStorage(summary.tag, idpValues)
    routeFn(idpValues)
  }

  return (
    <div id={id} className={sourceDropdown}>
      <Dropdown
        labelText={labelText}
        handleChange={toggle}
        isDisabled={possibleSources.length <= 1}
        value={selection}
        options={flatOptions(possibleSources)}
        isMultiSelect
        {...(possibleSources.length === 1 && selection.length >= 1
          ? // Length check above means this is safe
            {allSelectedText: selection[0]!.label}
          : {})} // hack to usefully display name of only value when disabled
      />
    </div>
  )
}
